/**
 * Инициализация i18next для survey-client.
 * Ресурсы подключаются из отдельных языковых папок по модели passport-client,
 * но содержат только минимальный словарь, реально используемый survey-client.
 *
 * @module locales
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { LOCALE_DEFAULT, LOCALE_SUPPORTED, localeNormalize, type TLocalesValues } from '../constants/locale';

import csTranslation from './cs/translation.json';
import deTranslation from './de/translation.json';
import enTranslation from './en/translation.json';
import esTranslation from './es/translation.json';
import plTranslation from './pl/translation.json';
import ptTranslation from './pt/translation.json';
import ruTranslation from './ru/translation.json';
import ukTranslation from './uk/translation.json';

const STORAGE_SURVEY_I18N_KEY = 'survey-client-i18next-lng';
type TranslationResource = typeof enTranslation;

/**
 * Возвращает стартовый язык: сохранённый в localStorage, иначе navigator, иначе дефолт.
 *
 * @returns {TLocalesValues}
 */
function getInitialLanguage(): TLocalesValues {
  if (typeof window === 'undefined') {
    return LOCALE_DEFAULT;
  }
  const stored = window.localStorage.getItem(STORAGE_SURVEY_I18N_KEY);
  if (stored !== null && stored !== '') {
    return localeNormalize(stored);
  }
  const nav = window.navigator.language;
  return localeNormalize(nav);
}

const resources: Record<TLocalesValues, { translation: TranslationResource }> = {
  cs: { translation: csTranslation },
  de: { translation: deTranslation },
  en: { translation: enTranslation },
  es: { translation: esTranslation },
  pl: { translation: plTranslation },
  pt: { translation: ptTranslation },
  ru: { translation: ruTranslation },
  uk: { translation: ukTranslation },
};

void i18n.use(initReactI18next).init({
  fallbackLng: LOCALE_DEFAULT,
  lng: getInitialLanguage(),
  load: 'currentOnly',
  supportedLngs: LOCALE_SUPPORTED,
  nonExplicitSupportedLngs: true,
  resources,
  debug: false,
  interpolation: {
    escapeValue: false,
    defaultVariables: {
      companyName: 'Mitgo',
      serviceName: 'Mitgo\u00A0ID',
    },
  },
  react: {
    useSuspense: false,
    transSupportBasicHtmlNodes: true,
    transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p'],
  },
});

if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('lang', getInitialLanguage());
}

i18n.on('languageChanged', (lng) => {
  if (typeof window === 'undefined') {
    return;
  }
  const normalized = localeNormalize(lng);
  window.localStorage.setItem(STORAGE_SURVEY_I18N_KEY, normalized);
  document.documentElement.setAttribute('lang', normalized);
});

export { default } from 'i18next';
